import { TQConnectSetupTabs } from '../../types/routes';
import {
  ITQShoutoutCategory,
  TQBadgeColorType,
  TQRatingCategory,
  TQRatingCategoryValue,
  TQShoutoutCategory,
} from '../../types/tqConnectTypes';
import {
  TQServiceRequestStatus,
  TQServiceRequestVisibility,
  TQServiceRequestVisibilityType,
} from '../../types/tqConnectTypes/service-requests';

// Shoutout Category Constants
export const SHOUTOUT_CATEGORY_LABELS: Record<string, string> = {
  [TQShoutoutCategory.MILESTONE_ACHIEVER]: 'Milestone Achiever',
  [TQShoutoutCategory.TOP_CONTRIBUTOR]: 'Top Contributor',
  [TQShoutoutCategory.CHALLENGE_CHAMP]: 'Challenge Champ',
  [TQShoutoutCategory.BIG_WIN]: 'Big Win',
  [TQShoutoutCategory.CONSISTENCY_STAR]: 'Consistency Star',
  [TQShoutoutCategory.CREATIVE_SPARK]: 'Creative Spark',
  [TQShoutoutCategory.SUPPORT_HERO]: 'Support Hero',
  [TQShoutoutCategory.COLLABORATION_STAR]: 'Collaboration Star',
  [TQShoutoutCategory.MOST_HELPFUL]: 'Most Helpful',
  [TQShoutoutCategory.GRATITUDE_NOTE]: 'Gratitude Note',
  [TQShoutoutCategory.CUSTOM]: 'Create your own',
};

export const SHOUTOUT_CATEGORY_EMOJIS: Record<string, string> = {
  [TQShoutoutCategory.MILESTONE_ACHIEVER]: '🎯',
  [TQShoutoutCategory.TOP_CONTRIBUTOR]: '⭐',
  [TQShoutoutCategory.CHALLENGE_CHAMP]: '🏆',
  [TQShoutoutCategory.BIG_WIN]: '🚀',
  [TQShoutoutCategory.CONSISTENCY_STAR]: '🔥',
  [TQShoutoutCategory.CREATIVE_SPARK]: '💡',
  [TQShoutoutCategory.SUPPORT_HERO]: '🛡️',
  [TQShoutoutCategory.COLLABORATION_STAR]: '🤝',
  [TQShoutoutCategory.MOST_HELPFUL]: '🙌',
  [TQShoutoutCategory.GRATITUDE_NOTE]: '💖',
  [TQShoutoutCategory.CUSTOM]: '📝',
};

// Rating Category Constants
export const RATING_CATEGORY_LABELS: Record<string, string> = {
  [TQRatingCategory.POOR]: 'Poor',
  [TQRatingCategory.OKAY]: "S'okay",
  [TQRatingCategory.GOOD]: 'Good',
  [TQRatingCategory.EXCELLENT]: 'Excellent',
  [TQRatingCategory.OUTSTANDING]: 'Outstanding',
};

export const RATING_CATEGORY_EMOJIS: Record<string, string> = {
  [TQRatingCategory.POOR]: '😞',
  [TQRatingCategory.OKAY]: '😐',
  [TQRatingCategory.GOOD]: '🙂',
  [TQRatingCategory.EXCELLENT]: '😄',
  [TQRatingCategory.OUTSTANDING]: '🤩',
};

export const RATING_CATEGORY_SCORES: Record<string, number> = {
  [TQRatingCategory.POOR]: 1,
  [TQRatingCategory.OKAY]: 2,
  [TQRatingCategory.GOOD]: 3,
  [TQRatingCategory.EXCELLENT]: 4,
  [TQRatingCategory.OUTSTANDING]: 5,
};

// Badge Level Constants
export const BADGE_LEVELS = [200, 500, 700, 900, 1000] as const;

export const DEFAULT_BADGE_COLORS: Record<number, string> = {
  200: '#ff9999', // Light red/pink
  500: '#ffcc99', // Light orange
  700: '#99ff99', // Light green
  900: '#cc99ff', // Light purple
  1000: '#99ccff', // Light blue
};

export const DEFAULT_BADGE_NAMES: Record<number, string> = {
  200: 'Getting Started',
  500: 'Reliable Member',
  700: 'Trusted Contributor',
  900: 'Credible Leader',
  1000: 'ILH Legend',
};

// TQ Score Configuration Constants
export const TQ_SCORE_CONFIG = {
  MAX_SCORE: 1000,
  DEFAULT_PROFILE_COMPLETION_WEIGHT: 20,
  DEFAULT_FEEDBACK_WEIGHT: 50,
  DEFAULT_SHOUTOUT_WEIGHT: 30,
  DEFAULT_SHOUTOUT_SCORE_EACH: 10,
  MIN_RATING_SCORE: 1,
  MAX_RATING_SCORE: 5,
} as const;

export const PAGE_LIMIT = 10;

// Validation Constants
export const TQ_VALIDATION = {
  MIN_TRUST_TRIGGER_WEIGHT: 5, // in percentage (5%)
  MIN_PROFILE_QUESTIONS_COUNT: 2,
  MAX_PROFILE_QUESTIONS_COUNT: 5,
  MAX_QUESTION_TITLE_LENGTH: 100,
  MAX_HELP_TEXT_LENGTH: 100,
  MAX_OPTION_TEXT_LENGTH: 50,
  MIN_SINGLE_SELECT_OPTIONS: 2,
  MIN_MULTIPLE_SELECT_OPTIONS: 3,
  MIN_SCORE: 0,
  MAX_SCORE: 1000,
  MIN_WEIGHT: 0,
  MAX_WEIGHT: 100,
  MAX_BADGE_NAME_LENGTH: 20,
  MAX_BADGES_COUNT: 10,
  MIN_BADGES_COUNT: 3,
  MAX_SERVICE_REQUEST_TITLE_LENGTH: 100,
  MAX_SERVICE_REQUEST_DESCRIPTION_LENGTH: 500,
  MAX_SERVICE_REQUEST_FILES_COUNT: 4,
  MAX_SERVICE_REQUEST_FILE_SIZE: 5 * 1024 * 1024, // 5 MB in bytes
  MAX_SERVICE_REQUEST_FILE_SIZE_IN_MB: 5, // 5 MB
  MAX_SERVICE_REQUEST_RESPONSE_DESCRIPTION_LENGTH: 250,
  MAX_SERVICE_REQUEST_RESPONSE_FEEDBACK_LENGTH: 250,
  MAX_SERVICE_REQUEST_RESPONSE_FEEDBACK_ATTACHMENTS_COUNT: 1,
  MAX_SHOUTOUT_CATEGORY_TITLE_LENGTH: 20,
  MAX_SHOUTOUT_DESCRIPTION_LENGTH: 500,
  MAX_SHOUTOUT_COVER_IMAGE_SIZE: 5 * 1024 * 1024, // 5 MB in bytes
} as const;

export const TQ_CONSTANTS = {
  DEFAULT_QUESTION_OPTIONS: Array(4).fill('') as string[],
};

export const TQConnectSetupTabsTitles: Record<TQConnectSetupTabs, string> = {
  [TQConnectSetupTabs.TRUST_TRIGGERS]: 'Trust Triggers',
  [TQConnectSetupTabs.TRUST_LEVELS]: 'Trust Levels',
};

export const TQ_RATING_COLORS_MAP: Record<
  TQRatingCategoryValue,
  {
    COLOR: string;
    COLOR_FILL: string;
  }
> = {
  [TQRatingCategory.POOR]: {
    COLOR: 'var(--error)',
    COLOR_FILL: 'var(--error-fill)',
  }, // red and light red (error)
  [TQRatingCategory.OKAY]: {
    COLOR: 'var(--tm-primary)',
    COLOR_FILL: 'var(--tm-primary-light)',
  }, // orange and light orange (tm primary)
  [TQRatingCategory.GOOD]: {
    COLOR: 'var(--warning500)',
    COLOR_FILL: 'var(--warning-fill)',
  }, // yellow and light yellow (warning)
  [TQRatingCategory.EXCELLENT]: {
    COLOR: 'var(--success)',
    COLOR_FILL: 'var(--success-fill)',
  }, // green and light green (success )
  [TQRatingCategory.OUTSTANDING]: {
    COLOR: 'var(--info)',
    COLOR_FILL: 'var(--info-fill)',
  }, // cyan and light blue (info)
};

export const ACCEPT_FILE_TYPES = {
  image: ['jpg', 'jpeg', 'png'],
  video: ['mp4'],
  docx: ['docx', 'doc', 'pptx', 'ppt', 'xlsx', 'xls', 'txt'],
  pdf: ['pdf'],
};

export const SERVICE_REQUEST_LIST_SCROLL =
  'service-requests-discover-scroll-container';

export const SERVICE_REQUEST_STATUS_COLORS: Record<
  TQServiceRequestStatus,
  string
> = {
  [TQServiceRequestStatus.OPEN]: 'default',
  [TQServiceRequestStatus.IN_PROGRESS]: 'info-fill',
  [TQServiceRequestStatus.COMPLETED]: 'success-fill',
  [TQServiceRequestStatus.REJECTED]: 'error-fill',
  [TQServiceRequestStatus.CLOSED]: 'default',
};

export const SERVICE_REQUESTS_LIMIT = 10;
export const SERVICE_REQUEST_RESPONSES_LIMIT = 10;
export const COMMUNITY_MAX_INPUT_MEDIA_FILE_SIZE = 50; // 50 MB
export const ACTIVITY_LOGS_LIMIT = 10;
export const PROFILE_REVIEWS_LIMIT = 10;
export const PROFILE_SHOUTOUTS_LIMIT = 10;
export const TQ_CONNECT_DRAWER_WIDTH = 400;
export const TQ_CONNECT_TOP_CONTRIBUTORS_COUNT = 5;

export const DEFAULT_CUSTOM_SHOUTOUT_CATEGORY: ITQShoutoutCategory = {
  name: 'Create your own',
  logo: '🏳️',
  label: 'What would you like to celebrate or highlight?',
  placeholder:
    'Describe the contribution, moment, or quality you’d like to recognize.',
};

export const TQ_BADGE_COLORS = [
  '#FEE2E2', // Light red/pink
  '#FFEFE1', // Light orange
  '#FEF9E9', // Light yellow
  '#E7FFE8', // Light green
  '#E1FFFE', // Light cyan
  '#D3E8FF', // Light blue
  '#E6E6FF', // Light purple
  '#F5E2FF', // Light pink
  '#FFDAEF', // Light lime
  '#FFDBDB', // Light coral
  '#FFF1DE', // Light lavender
  '#E2E8F0', // Light blue grey
] as const;

export const TQ_BADGE_COLORS_MAP: Record<
  TQBadgeColorType,
  {
    COLOR: string;
    COLOR_HIGHLIGHT: string;
  }
> = {
  '#FEE2E2': { COLOR: '#FEE2E2', COLOR_HIGHLIGHT: '#FF3B30' }, // Light red/pink
  '#FFEFE1': { COLOR: '#FFEFE1', COLOR_HIGHLIGHT: '#FF9500' }, // Light orange
  '#FEF9E9': { COLOR: '#FEF9E9', COLOR_HIGHLIGHT: '#FFCC00' }, // Light yellow
  '#E7FFE8': { COLOR: '#E7FFE8', COLOR_HIGHLIGHT: '#34C759' }, // Light green
  '#E1FFFE': { COLOR: '#E1FFFE', COLOR_HIGHLIGHT: '#00C7BE' }, // Light cyan
  '#D3E8FF': { COLOR: '#D3E8FF', COLOR_HIGHLIGHT: '#007AFF' }, // Light blue
  '#E6E6FF': { COLOR: '#E6E6FF', COLOR_HIGHLIGHT: '#5856D6' }, // Light purple
  '#F5E2FF': { COLOR: '#F5E2FF', COLOR_HIGHLIGHT: '#AF52DE' }, // Light pink
  '#FFDAEF': { COLOR: '#FFDAEF', COLOR_HIGHLIGHT: '#E12C93' }, // Light lime
  '#FFDBDB': { COLOR: '#FFDBDB', COLOR_HIGHLIGHT: '#993434' }, // Light coral
  '#FFF1DE': { COLOR: '#FFF1DE', COLOR_HIGHLIGHT: '#A2845E' }, // Light lavender
  '#E2E8F0': { COLOR: '#E2E8F0', COLOR_HIGHLIGHT: '#334155' }, // Light blue grey
};

export const TYPE_LABELS: Record<TQServiceRequestVisibilityType, string> = {
  [TQServiceRequestVisibility.PUBLIC]: 'PUBLIC REQUEST',
  [TQServiceRequestVisibility.PRIVATE]: 'PRIVATE REQUEST',
};
